import React, { useState } from 'react';

const JsonToXml: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [xmlOutput, setXmlOutput] = useState('');
  const [error, setError] = useState('');
  const [rootName, setRootName] = useState('root');

  const convertJsonToXml = (jsonStr: string, rootElementName: string): string => {
    try {
      // Parse JSON
      const jsonObj = JSON.parse(jsonStr);
      
      // Convert to XML
      const xml = convertObjectToXml(jsonObj, rootElementName);
      
      // Format XML
      return formatXml(xml);
    } catch (err) {
      throw new Error('Invalid JSON format');
    }
  };

  const convertObjectToXml = (obj: any, elementName: string): string => {
    if (obj === null || obj === undefined) {
      return `<${elementName}></${elementName}>`;
    }
    
    if (typeof obj === 'string') {
      return `<${elementName}>${escapeXml(obj)}</${elementName}>`;
    }
    
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return `<${elementName}>${obj}</${elementName}>`;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => convertObjectToXml(item, elementName)).join('\n');
    }
    
    if (typeof obj === 'object') {
      const attributes: string[] = [];
      const children: string[] = [];
      
      for (const [key, value] of Object.entries(obj)) {
        if (key.startsWith('@')) {
          // Handle attributes
          attributes.push(`${key.substring(1)}="${escapeXmlAttribute(String(value))}"`);
        } else if (key.startsWith('#')) {
          // Handle text content
          children.unshift(escapeXml(String(value)));
        } else {
          // Handle child elements
          children.push(convertObjectToXml(value, key));
        }
      }
      
      const attrStr = attributes.length > 0 ? ` ${attributes.join(' ')}` : '';
      const content = children.join('\n');
      
      if (children.length === 0) {
        return `<${elementName}${attrStr}/>`;
      } else if (children.length === 1 && typeof obj === 'string') {
        return `<${elementName}${attrStr}>${content}</${elementName}>`;
      } else {
        return `<${elementName}${attrStr}>\n${content}\n</${elementName}>`;
      }
    }
    
    return `<${elementName}></${elementName}>`;
  };

  const escapeXml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const escapeXmlAttribute = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const formatXml = (xml: string): string => {
    let formatted = '';
    let indent = '';
    const tab = '  ';
    
    xml.split(/>\s*</).forEach(node => {
      if (node.match(/^\/\w/)) {
        // Closing tag
        indent = indent.substring(tab.length);
      }
      formatted += indent + '<' + node + '>\r\n';
      if (node.match(/^<?\w[^>]*[^\/]$/)) {
        // Opening tag
        indent += tab;
      }
    });
    
    return formatted.substring(1, formatted.length - 3);
  };

  const handleConvert = () => {
    if (!jsonInput.trim()) {
      setError('请输入JSON数据');
      return;
    }
    
    try {
      setError('');
      const xml = convertJsonToXml(jsonInput, rootName);
      setXmlOutput(xml);
    } catch (err) {
      setError(err instanceof Error ? err.message : '转换失败');
      setXmlOutput('');
    }
  };

  const handleClear = () => {
    setJsonInput('');
    setXmlOutput('');
    setError('');
  };

  const handleCopyXml = () => {
    if (xmlOutput) {
      navigator.clipboard.writeText(xmlOutput);
    }
  };

  const handleDownloadXml = () => {
    if (xmlOutput) {
      const blob = new Blob([xmlOutput], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'converted.xml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleSampleJson = () => {
    const sample = `{
  "person": {
    "@id": "123",
    "@type": "employee",
    "name": "张三",
    "age": 30,
    "email": "zhangsan@example.com",
    "address": {
      "street": "中关村大街",
      "city": "北京",
      "country": "中国"
    },
    "hobbies": ["读书", "游泳", "编程"],
    "active": true
  }
}`;
    setJsonInput(sample);
    setRootName('data');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">JSON转XML工具</h1>
          <p className="text-lg text-gray-600">将JSON数据转换为XML格式，支持自定义根元素名称</p>
        </div>

        {/* Root Element Name */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center space-x-4">
            <label htmlFor="rootName" className="text-sm font-medium text-gray-700">
              根元素名称:
            </label>
            <input
              id="rootName"
              type="text"
              value={rootName}
              onChange={(e) => setRootName(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="root"
            />
            <button
              onClick={handleSampleJson}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              加载示例
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: JSON Input */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">JSON 输入</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  清空
                </button>
                <button
                  onClick={handleConvert}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  转换
                </button>
              </div>
            </div>
            
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="在此输入JSON数据..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Right: XML Output */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">XML 输出</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleCopyXml}
                  disabled={!xmlOutput}
                  className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  复制
                </button>
                <button
                  onClick={handleDownloadXml}
                  disabled={!xmlOutput}
                  className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下载
                </button>
              </div>
            </div>
            
            <textarea
              value={xmlOutput}
              readOnly
              placeholder="转换后的XML将显示在这里..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
            />
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🔄</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">智能转换</h3>
            <p className="text-gray-600">自动识别JSON结构，生成格式化的XML</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">⚙️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">自定义配置</h3>
            <p className="text-gray-600">支持自定义根元素名称，满足不同需求</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📋</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">便捷操作</h3>
            <p className="text-gray-600">支持复制、下载，提供示例数据</p>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">使用说明</h3>
          <ul className="text-blue-700 space-y-2 text-sm">
            <li>• 输入有效的JSON数据，点击转换按钮</li>
            <li>• 可以自定义根元素名称，默认为"root"</li>
            <li>• 支持数组、对象、字符串、数字、布尔值等数据类型</li>
            <li>• 生成的XML会自动格式化，便于阅读</li>
            <li>• 可以复制XML结果或下载为文件</li>
            <li>• 点击"加载示例"查看转换效果</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default JsonToXml;
